import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  List,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { getThemeTokens } from '../../theme/getLightTheme';
import { toolNavItems, toolPaths } from './toolNavItems';

const NAV_MAX_WIDTH = 1280;
const TOOLS_KEY = 'tools-menu';

interface PrimaryLink {
  label: string;
  path: string;
}

const primaryLinks: PrimaryLink[] = [
  { label: 'Home', path: '/' },
  { label: 'Tools', path: TOOLS_KEY },
  { label: 'Global Trade Trends', path: '/trade-trends' },
  { label: 'News & Insights', path: '/news' },
  { label: 'Resources', path: '/resources' },
  { label: 'About', path: '/about' },
];

export const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const tokens = getThemeTokens;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [toolsAnchor, setToolsAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isToolsActive = toolPaths.includes(location.pathname);
  const isLinkActive = (link: PrimaryLink) =>
    link.path === TOOLS_KEY ? isToolsActive : location.pathname === link.path;

  const go = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
    setToolsAnchor(null);
  };

  // The newsletter box (NewsletterCta, id="newsletter") is mounted on every
  // page via Layout, so this scrolls in place rather than routing to "/".
  const goToNewsletter = () => {
    const el = document.getElementById('newsletter');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#newsletter');
    }
    setDrawerOpen(false);
    setToolsAnchor(null);
  };

  const logo = (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', minWidth: 0 }}
      onClick={() => go('/')}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: tokens.logoAccent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Box component="svg" viewBox="0 0 64 64" sx={{ width: 24, height: 24 }} aria-hidden>
          <g fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 15 H16 L23 21" />
            <circle cx="33" cy="27" r="13" />
            <ellipse cx="33" cy="27" rx="5.5" ry="13" />
            <line x1="20" y1="27" x2="46" y2="27" />
            <path d="M25 39 V47" />
            <path d="M41 39 V47" />
          </g>
          <circle cx="25" cy="50" r="3.2" fill="#FFFFFF" />
          <circle cx="41" cy="50" r="3.2" fill="#FFFFFF" />
        </Box>
      </Box>
      <Typography
        variant="subtitle1"
        component="span"
        sx={{
          fontFamily: tokens.typography.fontFamilyDisplay,
          fontWeight: 600,
          color: tokens.textPrimary,
          fontSize: '1.125rem',
          letterSpacing: '-0.01em',
          display: { xs: 'none', sm: 'block' },
        }}
        noWrap
      >
        Global Cart Kit
      </Typography>
    </Box>
  );

  const desktopLinks = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {primaryLinks.map((link) => {
        const active = isLinkActive(link);
        const isTools = link.path === TOOLS_KEY;
        return (
          <Button
            key={link.label}
            onClick={(e) =>
              isTools ? setToolsAnchor(e.currentTarget) : go(link.path)
            }
            endIcon={isTools ? <KeyboardArrowDown fontSize="small" /> : undefined}
            disableRipple
            sx={{
              px: 1.5,
              height: 64,
              borderRadius: 0,
              color: active ? tokens.primary : tokens.textPrimary,
              fontWeight: active ? 600 : 500,
              fontSize: '0.9375rem',
              borderBottom: '2px solid',
              borderColor: active ? tokens.primary : 'transparent',
              '&:hover': {
                bgcolor: 'transparent',
                color: tokens.primary,
                borderColor: active ? tokens.primary : alpha(tokens.primary, 0.3),
              },
            }}
          >
            {link.label}
          </Button>
        );
      })}
    </Box>
  );

  const toolsMenu = (
    <Menu
      anchorEl={toolsAnchor}
      open={Boolean(toolsAnchor)}
      onClose={() => setToolsAnchor(null)}
      MenuListProps={{ sx: { py: 0.5, minWidth: 240 } }}
    >
      {toolNavItems.map((item) => (
        <MenuItem
          key={item.path}
          selected={location.pathname === item.path}
          onClick={() => go(item.path)}
          sx={{ py: 1 }}
        >
          <ListItemIcon sx={{ color: tokens.textSecondary, minWidth: 36 }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: '0.875rem' }}
          />
          {item.isWip && (
            <Box
              component="span"
              sx={{
                ml: 1.5,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.625rem',
                fontWeight: 600,
                bgcolor: alpha(tokens.warning, 0.15),
                color: tokens.warning,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              WIP
            </Box>
          )}
        </MenuItem>
      ))}
    </Menu>
  );

  const mobileDrawer = (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{ sx: { width: '100%', maxWidth: '100%' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            height: 56,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          {logo}
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close menu">
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={goToNewsletter}
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Get the briefing
          </Button>
          <List disablePadding>
            {primaryLinks
              .filter((l) => l.path !== TOOLS_KEY)
              .map((link) => (
                <ListItemButton
                  key={link.label}
                  selected={isLinkActive(link)}
                  onClick={() => go(link.path)}
                  sx={{ borderRadius: 1.5, mb: 0.5 }}
                >
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                  />
                </ListItemButton>
              ))}
          </List>

          <Divider sx={{ my: 2 }} />
          <Typography
            variant="overline"
            sx={{ px: 2, color: tokens.textMuted, fontWeight: 600 }}
          >
            Tools
          </Typography>
          <List disablePadding sx={{ mt: 0.5 }}>
            {toolNavItems.map((item) => (
              <ListItemButton
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => go(item.path)}
                sx={{ borderRadius: 1.5, mb: 0.5 }}
              >
                <ListItemIcon sx={{ color: tokens.textSecondary, minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.9375rem' }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <AppBar position="sticky" elevation={0} color="inherit">
      <Box sx={{ maxWidth: NAV_MAX_WIDTH, width: '100%', mx: 'auto' }}>
        <Toolbar sx={{ minHeight: { xs: 56, md: 64 }, px: { xs: 2, sm: 3 }, gap: 2 }}>
          {logo}

          {!isMobile && (
            <>
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                {desktopLinks}
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={goToNewsletter}
                sx={{ px: 2.25, py: 0.75, flexShrink: 0, fontWeight: 600 }}
              >
                Get the briefing
              </Button>
              {toolsMenu}
            </>
          )}

          {isMobile && (
            <>
              <Box sx={{ flex: 1 }} />
              <IconButton
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
                sx={{ color: tokens.textPrimary }}
              >
                <MenuIcon />
              </IconButton>
              {mobileDrawer}
            </>
          )}
        </Toolbar>
      </Box>
    </AppBar>
  );
};
